import React, { useState, useMemo } from 'react';
import { 
  Users, Clock, AlertCircle, CheckSquare, Square, Search, 
  Calendar, BookOpen, FileText 
} from 'lucide-react';
import { CSV_CONTENT } from './timetableData';

const NewTimetable = () => {
  const [viewMode, setViewMode] = useState('teachers');
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedGroupsForFilter, setSelectedGroupsForFilter] = useState([]); 
  const [selectedSubjectsForFilter, setSelectedSubjectsForFilter] = useState([]); 
  const [selectedClasses, setSelectedClasses] = useState([]); 
  const [searchTerm, setSearchTerm] = useState('');

  const csvData = useMemo(() => {
    if (!CSV_CONTENT || typeof CSV_CONTENT !== 'string') return [];
    
    // Clean CSV content (remove the variable declaration part if accidentally included)
    const rawContent = CSV_CONTENT.replace('const CSV_CONTENT = `', '').replace('`;', '').trim();
    
    const lines = rawContent.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length === 0) return [];

    const headers = lines[0].split(';').map(h => h.trim());
    
    const parsedData = lines.slice(1).map(line => {
      const values = line.split(';');
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] ? values[index].trim() : '';
      });
      return obj;
    });

    return parsedData.filter(d => {
      const teacherName = (d.teacher_name || '').toLowerCase();
      const subjectName = (d.subject_name || '').toLowerCase();
      const displayTitle = (d.display_title || '').toLowerCase();
      
      const isTatiana = teacherName.includes('tatiana costa');
      const isEAL = subjectName.includes('eal') || displayTitle.includes('eal');
      const isB2Prep = subjectName.includes('b2 prep') || displayTitle.includes('b2 prep');
      
      return !isTatiana && !isEAL && !isB2Prep;
    });
  }, []);

  const { 
    teachers, days, times, teacherStats, filteredTeachers, allGroups, groupStats, filteredGroups
  } = useMemo(() => {
    if (!csvData || csvData.length === 0) {
        return { teachers: [], days: [], times: [], teacherStats: {}, filteredTeachers: [], allGroups: [], groupStats: {}, filteredGroups: [] };
    }

    const uniqueTeachers = [...new Set(csvData.map(d => d.teacher_name).filter(Boolean))].sort();
    const filteredT = uniqueTeachers.filter(t => t?.toLowerCase().includes((searchTerm || '').toLowerCase()));

    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const uniqueDays = [...new Set(csvData.map(d => d.weekday_key).filter(Boolean))].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
    const uniqueTimes = [...new Set(csvData.map(d => d.start_time).filter(Boolean))].sort();

    const uniqueGroupsSet = new Set();
    csvData.forEach(d => {
      if (d && typeof d.group_codes === 'string' && d.group_codes.trim() !== '') {
        d.group_codes.split(',').forEach(g => uniqueGroupsSet.add(g.trim()));
      }
    });
    
    const uniqueAllGroups = Array.from(uniqueGroupsSet).filter(Boolean).filter(g => /^[6-9][A-Za-z]$/.test(g)).sort();
    const filteredG = uniqueAllGroups.filter(g => g?.toLowerCase().includes((searchTerm || '').toLowerCase()));

    const tStats = {};
    uniqueTeachers.forEach(teacher => {
      const teacherEvents = csvData.filter(d => d.teacher_name === teacher);
      const classEvents = teacherEvents.filter(d => {
        const type = (d.activity_type || '').toLowerCase();
        return type === 'class' || type === 'assembly';
      });
      
      let classCount = 0;
      classEvents.forEach(d => {
        const duration = parseInt(d.duration_minutes) || 60;
        classCount += (duration / 60);
      });
      
      const groupsSet = new Set();
      teacherEvents.forEach(d => {
        if (d && typeof d.group_codes === 'string' && d.group_codes.trim() !== '') {
            d.group_codes.split(',').forEach(c => groupsSet.add(c.trim()));
        }
      });

      tStats[teacher] = { totalClasses: classCount, groups: Array.from(groupsSet).filter(Boolean).sort() };
    });

    const gStats = {};
    uniqueAllGroups.forEach(group => {
      const groupEvents = csvData.filter(d => d && typeof d.group_codes === 'string' && d.group_codes.split(',').map(s=>s.trim()).includes(group));
      const classEvents = groupEvents.filter(d => {
        const type = (d.activity_type || '').toLowerCase();
        return type === 'class' || type === 'assembly';
      });
      
      const uniqueSlots = new Map();
      const subjectsSet = new Set();
      
      classEvents.forEach(e => {
        const key = `${e.weekday_key}-${e.start_time}`;
        const duration = parseInt(e.duration_minutes) || 60;
        if (!uniqueSlots.has(key)) { uniqueSlots.set(key, duration); } 
        else { uniqueSlots.set(key, Math.max(uniqueSlots.get(key), duration)); }
        if (e.subject_name) { subjectsSet.add(e.subject_name.trim()); }
      });
      
      let classCount = 0;
      uniqueSlots.forEach(duration => { classCount += (duration / 60); });
      gStats[group] = { totalClasses: classCount, subjects: Array.from(subjectsSet).filter(Boolean).sort() };
    });

    return { teachers: uniqueTeachers, days: uniqueDays, times: uniqueTimes, teacherStats: tStats, filteredTeachers: filteredT, allGroups: uniqueAllGroups, groupStats: gStats, filteredGroups: filteredG };
  }, [csvData, searchTerm]);

  const getColor = (key, isActivity = true) => {
    if (!isActivity) return 'bg-gray-100 text-gray-500 border-gray-300 border-dashed'; 
    if (!key) return 'bg-slate-100 text-slate-800 border-slate-300'; 
    
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-300', 'bg-green-100 text-green-800 border-green-300',
      'bg-purple-100 text-purple-800 border-purple-300', 'bg-orange-100 text-orange-800 border-orange-300',
      'bg-pink-100 text-pink-800 border-pink-300', 'bg-teal-100 text-teal-800 border-teal-300',
      'bg-yellow-100 text-yellow-800 border-yellow-300', 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'bg-rose-100 text-rose-800 border-rose-300', 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'bg-emerald-100 text-emerald-800 border-emerald-300'
    ];
    
    let hash = 0;
    for (let i = 0; i < key.length; i++) { hash = key.charCodeAt(i) + ((hash << 5) - hash); }
    return colors[Math.abs(hash) % colors.length];
  };

  const toggleTeacher = (teacher) => { setSelectedTeachers(prev => prev.includes(teacher) ? prev.filter(t => t !== teacher) : [...prev, teacher]); };
  const toggleGroupForFilter = (group) => { setSelectedGroupsForFilter(prev => prev.includes(group) ? prev.filter(g => g !== group) : [...prev, group]); };
  const toggleSubjectForFilter = (subject) => { setSelectedSubjectsForFilter(prev => prev.includes(subject) ? prev.filter(s => s !== subject) : [...prev, subject]); };
  const toggleClass = (cls) => { setSelectedClasses(prev => prev.includes(cls) ? prev.filter(c => c !== cls) : [...prev, cls]); };
  
  const toggleAll = () => {
    if (viewMode === 'teachers') {
      if (selectedTeachers.length === filteredTeachers.length) setSelectedTeachers([]);
      else setSelectedTeachers(filteredTeachers);
    } else if (viewMode === 'groups') {
      if (selectedClasses.length === filteredGroups.length) setSelectedClasses([]);
      else setSelectedClasses(filteredGroups);
    }
  };

  const dayNamesPt = { 'monday': 'Segunda', 'tuesday': 'Terça', 'wednesday': 'Quarta', 'thursday': 'Quinta', 'friday': 'Sexta', 'saturday': 'Sábado', 'sunday': 'Domingo' };

  const START_HOUR = 7; const END_HOUR = 18; const PIXELS_PER_MINUTE = 2; 

  const timeToMinutes = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };

  const checkLunchBreak = (events, proposedShift) => {
    const LUNCH_START = 720; const LUNCH_END = 900;
    const busyBlocks = events
      .filter(e => {
          const type = (e.activity_type || '').toLowerCase(); 
          const title = (e.display_title || '').toLowerCase();
          return !type.includes('free') && !title.includes('janela');
      })
      .map(e => {
          const s = timeToMinutes(e.start_time); const d = parseInt(e.duration_minutes) || 60;
          return [s, s + d];
      });

    if (proposedShift) { busyBlocks.push([proposedShift.startMins, proposedShift.endMins]); }

    const lunchBlocks = busyBlocks.map(([s, e]) => [Math.max(s, LUNCH_START), Math.min(e, LUNCH_END)]).filter(([s, e]) => s < e).sort((a, b) => a[0] - b[0]);
    const merged = [];
    if (lunchBlocks.length > 0) {
        let current = [...lunchBlocks[0]];
        for (let i = 1; i < lunchBlocks.length; i++) {
            if (lunchBlocks[i][0] <= current[1]) { current[1] = Math.max(current[1], lunchBlocks[i][1]); } 
            else { merged.push(current); current = [...lunchBlocks[i]]; }
        }
        merged.push(current);
    }

    let currentTime = LUNCH_START;
    for (const [start, end] of merged) {
        if (start - currentTime >= 60) return true;
        currentTime = end;
    }
    if (LUNCH_END - currentTime >= 60) return true;
    return false; 
  };

  const hoursRange = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR);

  const FULL_TIME_TEACHERS = [
    "Alexandra Borges", "Ana Alqueres", "Andre Simao", "Deborah Oliveira", 
    "Delnice Pereira", "Fernanda Andrade", "Gustavo Duarte", "Jamil Santos", 
    "Leandro Melo", "Luana Benevides", "Luiza Limeira", "Maria Bárbara Branco", 
    "Maria Clara Fonseca PEREIRA", "Mariana Corrêa", "Nathalia Bardella", 
    "Nathalia Gonçalves", "Palloma Pimentel", "Taina Lopes", "Vinicius Rocha"
  ];

  const COORDINATORS = [
    "Cris Diniz", "Cristiana Rodrigues", "Fernanda Andrade", "Juliana Braz",
    "Luiz Rafael da Silva", "Paula Pessoa", "Sarah Pinto", "Thamires Prudente"
  ];

  const activeEntities = viewMode === 'teachers' ? selectedTeachers : selectedClasses;
  const searchPlaceholder = viewMode === 'teachers' ? 'Buscar professor...' : 'Buscar turma...';
  const listData = viewMode === 'teachers' ? filteredTeachers : filteredGroups;

  return (
    <div className="h-full bg-slate-50 font-sans flex flex-col md:flex-row overflow-hidden rounded-xl border border-slate-200">
      
      <aside className="w-full md:w-[320px] bg-white border-r border-slate-200 flex flex-col h-auto md:h-full flex-shrink-0 z-20">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="grid grid-cols-4 bg-slate-200 p-1 rounded-lg gap-1">
             <button onClick={() => { setViewMode('teachers'); setSearchTerm(''); setSelectedSubjectsForFilter([]); }} className={`py-1.5 px-1 text-[10px] font-bold flex flex-col items-center justify-center rounded-md transition-all ${viewMode === 'teachers' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}><Users className="w-4 h-4 mb-0.5" /> Profs</button>
             <button onClick={() => { setViewMode('groups'); setSearchTerm(''); setSelectedGroupsForFilter([]); }} className={`py-1.5 px-1 text-[10px] font-bold flex flex-col items-center justify-center rounded-md transition-all ${viewMode === 'groups' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}><BookOpen className="w-4 h-4 mb-0.5" /> Turmas</button>
             <button onClick={() => { setViewMode('community'); setSearchTerm(''); }} className={`py-1.5 px-1 text-[10px] font-bold flex flex-col items-center justify-center rounded-md transition-all ${viewMode === 'community' ? 'bg-white shadow-sm text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}><Clock className="w-4 h-4 mb-0.5" /> Commu.</button>
             <button onClick={() => { setViewMode('cycletest'); setSearchTerm(''); }} className={`py-1.5 px-1 text-[10px] font-bold flex flex-col items-center justify-center rounded-md transition-all ${viewMode === 'cycletest' ? 'bg-white shadow-sm text-purple-700' : 'text-slate-500 hover:text-slate-700'}`}><FileText className="w-4 h-4 mb-0.5" /> Cycle</button>
          </div>
        </div>

        {viewMode === 'community' ? (
          <div className="p-4 text-sm text-slate-600 leading-relaxed overflow-y-auto">
             <div className="bg-blue-50 text-blue-800 p-4 rounded-xl border border-blue-100 mb-4">
               <AlertCircle className="w-5 h-5 mb-2 text-blue-600" />
               <strong>Community Time</strong>
               <div className="mt-2 space-y-1">
                 <p className="text-xs font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 bg-blue-400 rounded-full inline-block"></span> 08:30 - 09:00 (6º e 7º)</p>
                 <p className="text-xs font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 bg-blue-400 rounded-full inline-block"></span> 13:00 - 13:30 (8º e 9º)</p>
               </div>
             </div>
             <p className="text-xs text-slate-500">Ao lado, você visualiza a escala completa de professores disponíveis para o Community Time em cada dia da semana.</p>
          </div>
        ) : viewMode === 'cycletest' ? (
          <div className="p-4 text-sm text-slate-600 leading-relaxed overflow-y-auto">
             <div className="bg-purple-50 text-purple-800 p-4 rounded-xl border border-purple-100 mb-4">
               <AlertCircle className="w-5 h-5 mb-2 text-purple-600" />
               <strong>Cycle Test</strong>
               <div className="mt-2 space-y-1">
                 <p className="text-xs font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 bg-purple-400 rounded-full inline-block"></span> 4ª F: 09:00 - 10:00 (6º e 7º)</p>
                 <p className="text-xs font-medium flex items-center gap-1"><span className="w-1.5 h-1.5 bg-purple-400 rounded-full inline-block"></span> 5ª F: 09:30 - 10:30 (8º e 9º)</p>
               </div>
             </div>
             <p className="text-xs text-slate-500">Mapeamento de professores do turno da manhã livres para fiscalização.</p>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-200">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input type="text" placeholder={searchPlaceholder} className="w-full pl-9 pr-3 py-2 bg-slate-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              <button onClick={toggleAll} className="w-full flex items-center p-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md mb-2 font-medium">
                {activeEntities.length === listData.length && listData.length > 0 ? <CheckSquare className="w-4 h-4 mr-2 text-blue-600" /> : <Square className="w-4 h-4 mr-2 text-slate-400" />}
                Selecionar Todos ({listData.length})
              </button>

              {listData.map(item => {
                const isSelected = activeEntities.includes(item);
                const teacherDataArr = viewMode === 'teachers' ? teacherStats[item] : null;
                const classDataArr = viewMode === 'groups' ? groupStats[item] : null;
                const totalClasses = (viewMode === 'teachers' ? teacherDataArr?.totalClasses : classDataArr?.totalClasses) || 0;

                return (
                  <div key={item} onClick={() => viewMode === 'teachers' ? toggleTeacher(item) : toggleClass(item)} className={`flex flex-col p-3 mb-1 rounded-lg cursor-pointer transition-all border ${isSelected ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-3 overflow-hidden ${isSelected ? 'text-blue-800' : 'text-slate-700'}`}>
                        {isSelected ? <CheckSquare className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <Square className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                        <span className="text-sm font-medium truncate">{item}</span>
                      </div>
                      <div className={`text-xs font-bold px-2 py-1 bg-white bg-opacity-60 rounded-full flex-shrink-0 ${isSelected ? 'text-blue-800' : 'text-slate-600'}`}>{totalClasses} <span className="font-normal opacity-70">aulas</span></div>
                    </div>

                    {viewMode === 'teachers' && isSelected && teacherDataArr?.groups?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-200/60 flex flex-wrap gap-1.5" onClick={e => e.stopPropagation()}>
                        <span className="w-full text-[9px] uppercase tracking-wider text-blue-500/80 font-bold mb-0.5">Turmas:</span>
                        {teacherDataArr.groups.map(group => {
                          const isGroupSelected = selectedGroupsForFilter.includes(group);
                          return <button key={group} onClick={(e) => { e.stopPropagation(); toggleGroupForFilter(group); }} className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md border transition-colors ${isGroupSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-100'}`}>{group}</button>
                        })}
                      </div>
                    )}

                    {viewMode === 'groups' && isSelected && classDataArr?.subjects?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-blue-200/60 flex flex-wrap gap-1.5" onClick={e => e.stopPropagation()}>
                        <span className="w-full text-[9px] uppercase tracking-wider text-blue-500/80 font-bold mb-0.5">Disciplinas:</span>
                        {classDataArr.subjects.map(subject => {
                          const isSubjectSelected = selectedSubjectsForFilter.includes(subject);
                          return <button key={subject} onClick={(e) => { e.stopPropagation(); toggleSubjectForFilter(subject); }} className={`px-1.5 py-0.5 text-[10px] font-bold rounded-md border transition-colors ${isSubjectSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-100'}`}>{subject}</button>
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </aside>

      <main className="flex-1 p-4 md:p-6 bg-slate-50 flex flex-col overflow-hidden overflow-y-auto">
        {viewMode === 'community' ? (
          <div className="max-w-6xl mx-auto w-full">
            <div className="flex items-center gap-4 mb-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <div className="bg-blue-100 p-3 rounded-full"><Clock className="w-8 h-8 text-blue-600" /></div>
              <div><h2 className="text-2xl font-bold text-slate-800">Escala de Community Time</h2><p className="text-slate-500 mt-1">Disponibilidade de professores.</p></div>
            </div>
            
            {[ { title: '6º e 7º anos (08:30 - 09:00)', startMins: 510, endMins: 540 }, { title: '8º e 9º anos (13:00 - 13:30)', startMins: 780, endMins: 810 } ].map(shift => (
              <div key={shift.title} className="mb-10">
                <h3 className="text-lg font-bold text-slate-700 mb-4 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span>{shift.title}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  {days.map(day => {
                     const availableTeachers = teachers.filter(t => {
                       if (COORDINATORS.includes(t)) return false;
                       const events = csvData.filter(d => d.teacher_name === t && d.weekday_key === day);
                       if (events.length === 0) return false; 
                       if (!checkLunchBreak(events, shift)) return false;
                       const isFullTime = FULL_TIME_TEACHERS.includes(t);
                       const isBusy = events.some(e => {
                         const type = (e.activity_type || '').toLowerCase(); 
                         const isClass = type === 'class' || type === 'assembly';
                         if (!isClass) return false; 
                         const sMins = timeToMinutes(e.start_time); const eMins = sMins + (parseInt(e.duration_minutes) || 60);
                         return sMins < shift.endMins && eMins > shift.startMins;
                       });
                       if (isBusy) return false; 
                       const hasExplicitBlock = events.some(e => {
                           const sMins = timeToMinutes(e.start_time); const eMins = sMins + (parseInt(e.duration_minutes) || 60);
                           return sMins < shift.endMins && eMins > shift.startMins;
                       });
                       const hasEventBefore = events.some(e => {
                           const sMins = timeToMinutes(e.start_time); const eMins = sMins + (parseInt(e.duration_minutes) || 60);
                           return eMins <= shift.startMins; 
                       });
                       const hasEventAfter = events.some(e => {
                           const sMins = timeToMinutes(e.start_time);
                           return sMins >= shift.endMins; 
                       });
                       const isJanela = hasEventBefore && hasEventAfter;
                       return hasExplicitBlock || isJanela || isFullTime;
                     }).sort();

                     return (
                       <div key={day} className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden hover:border-blue-200">
                         <div className="bg-slate-100 border-b border-slate-200 p-3 text-center"><span className="font-bold text-slate-700 capitalize text-xs">{dayNamesPt[day]}</span></div>
                         <div className="p-3 flex-1 flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                           {availableTeachers.length > 0 ? (
                             availableTeachers.map(t => (
                               <div key={t} className="flex items-center gap-2 p-2 rounded-lg bg-blue-50/50 border border-blue-100/50 text-xs text-blue-900 font-medium truncate">
                                 <Users className="w-3 h-3 text-blue-500 flex-shrink-0" />{t}
                               </div>
                             ))
                           ) : (
                             <div className="text-center text-slate-400 text-[10px] py-4 italic">Nenhum professor livre</div>
                           )}
                         </div>
                       </div>
                     );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'cycletest' ? (
          <div className="max-w-6xl mx-auto w-full">
            <div className="flex items-center gap-4 mb-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <div className="bg-purple-100 p-3 rounded-full"><FileText className="w-8 h-8 text-purple-600" /></div>
              <div><h2 className="text-2xl font-bold text-slate-800">Escala de Cycle Test</h2><p className="text-slate-500 mt-1">Fiscais disponíveis.</p></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[ { title: '6º e 7º anos', day: 'wednesday', dayName: 'Quarta-feira', time: '09:00 - 10:00', startMins: 540, endMins: 600 }, { title: '8º e 9º anos', day: 'thursday', dayName: 'Quinta-feira', time: '09:30 - 10:30', startMins: 570, endMins: 630 } ].map(shift => {
                  const availableTeachers = teachers.filter(t => {
                     if (COORDINATORS.includes(t)) return false;
                     const events = csvData.filter(d => d.teacher_name === t && d.weekday_key === shift.day);
                     if (events.length === 0) return false;
                     if (!checkLunchBreak(events, shift)) return false;
                     const isFullTime = FULL_TIME_TEACHERS.includes(t);
                     const isBusy = events.some(e => {
                       const type = (e.activity_type || '').toLowerCase(); 
                       const isClass = type === 'class' || type === 'assembly';
                       if (!isClass) return false; 
                       const sMins = timeToMinutes(e.start_time); const eMins = sMins + (parseInt(e.duration_minutes) || 60);
                       return sMins < shift.endMins && eMins > shift.startMins;
                     });
                     if (isBusy) return false;
                     const hasEventBefore = events.some(e => {
                         const sMins = timeToMinutes(e.start_time); const eMins = sMins + (parseInt(e.duration_minutes) || 60);
                         return eMins <= shift.startMins; 
                     });
                     const hasEventAfter = events.some(e => {
                         const sMins = timeToMinutes(e.start_time);
                         return sMins >= shift.endMins && sMins < 780; 
                     });
                     return hasEventBefore || hasEventAfter || isFullTime;
                  }).sort();

                  return (
                     <div key={shift.title} className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden hover:border-purple-300">
                       <div className="bg-purple-50 border-b border-purple-100 p-5 text-center"><h3 className="font-bold text-purple-900 text-lg">{shift.title}</h3><p className="text-purple-600 font-medium text-sm mt-1">{shift.dayName} • {shift.time}</p></div>
                       <div className="p-4 flex-1 flex flex-col gap-2 max-h-[400px] overflow-y-auto font-medium text-slate-700">
                         {availableTeachers.length > 0 ? (
                           availableTeachers.map(t => (
                             <div key={t} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 text-sm hover:bg-purple-50">
                               <Users className="w-4 h-4 text-purple-400" />{t}
                             </div>
                           ))
                         ) : (
                           <div className="text-center text-slate-400 py-8 italic">Nenhum professor livre</div>
                         )}
                       </div>
                     </div>
                  );
              })}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {activeEntities.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 space-y-4 py-20">
                <Calendar className="w-16 h-16 text-slate-300" /><h2 className="text-xl font-medium text-slate-600">Nenhum {viewMode === 'teachers' ? 'professor' : 'turma'} selecionado</h2>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col flex-1 overflow-hidden">
                <div className="flex border-b border-slate-200 bg-slate-100 z-30 flex-shrink-0">
                  <div className="w-16 md:w-20 flex-shrink-0 border-r border-slate-200 flex flex-col justify-end pb-2 items-center bg-slate-100"><Clock className="w-4 h-4 text-slate-400 mb-1" /><span className="text-[10px] font-bold text-slate-500 uppercase">Horário</span></div>
                  <div className="flex flex-1 overflow-x-auto">
                    {days.map(day => (
                      <div key={day} className="flex-1 min-w-[150px] border-r border-slate-200 last:border-none flex flex-col">
                        <div className="h-10 flex items-center justify-center font-bold text-slate-700 capitalize text-sm">{dayNamesPt[day] || day}</div>
                        {activeEntities.length > 1 && (
                          <div className="flex border-t border-slate-200 bg-slate-50 h-7">
                            {activeEntities.map(entity => (
                              <div key={entity} className="flex-1 flex items-center justify-center text-[10px] font-bold text-slate-600 border-r border-slate-200 last:border-none truncate px-1" title={entity}>{viewMode === 'teachers' ? (entity ? entity.split(' ')[0] : '') : entity}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-1 overflow-y-auto overflow-x-auto relative">
                  <div className="w-16 md:w-20 flex-shrink-0 border-r border-slate-200 bg-slate-50 relative z-20 sticky left-0">
                    {hoursRange.map(hour => (
                      <div key={hour} className="absolute w-full text-right pr-2 text-xs text-slate-500 font-medium" style={{ top: `${(hour - START_HOUR) * 60 * PIXELS_PER_MINUTE}px`, transform: 'translateY(-50%)' }}>{`${hour.toString().padStart(2, '0')}:00`}</div>
                    ))}
                  </div>

                  <div className="flex flex-1 relative min-w-max" style={{ height: `${(END_HOUR - START_HOUR) * 60 * PIXELS_PER_MINUTE + 60}px` }}>
                    <div className="absolute inset-0 pointer-events-none z-0">
                      {hoursRange.map(hour => (
                        <React.Fragment key={hour}>
                          <div className="absolute w-full border-t border-slate-200" style={{ top: `${(hour - START_HOUR) * 60 * PIXELS_PER_MINUTE}px` }} />
                          {hour !== END_HOUR && <div className="absolute w-full border-t border-slate-100 border-dashed" style={{ top: `${((hour - START_HOUR) * 60 + 30) * PIXELS_PER_MINUTE}px` }} />}
                        </React.Fragment>
                      ))}
                    </div>

                    {days.map(day => (
                      <div key={day} className="flex-1 min-w-[150px] border-r border-slate-200 last:border-none relative z-10">
                        <div className="absolute inset-0 flex">
                          {activeEntities.map(entity => {
                            const entityEvents = csvData.filter(d => {
                              if (d.weekday_key !== day) return false;
                              if (viewMode === 'teachers') return d.teacher_name === entity;
                              if (viewMode === 'groups') { return d && typeof d.group_codes === 'string' && d.group_codes.split(',').map(s=>s.trim()).includes(entity); }
                              return false;
                            });

                            const isLowerMiddleSchool = /^[67][A-Za-z]$/.test(entity);
                            if (viewMode === 'groups' && isLowerMiddleSchool) {
                                const hasBlockAt830 = entityEvents.some(e => e.start_time === '08:30');
                                if (!hasBlockAt830) {
                                    entityEvents.push({ start_time: '08:30', end_time: '09:00', duration_minutes: '30', activity_type: 'community_time', display_title: 'Community Time', subject_name: 'Community Time', teacher_name: 'Prof. Escalados', group_codes: entity, weekday_key: day });
                                }
                                if (day === 'wednesday') {
                                    const hasBlockAt900 = entityEvents.some(e => e.start_time === '09:00');
                                    if (!hasBlockAt900) {
                                        entityEvents.push({ start_time: '09:00', end_time: '10:00', duration_minutes: '60', activity_type: 'test', display_title: 'Cycle Test', subject_name: 'Cycle Test', teacher_name: 'Prof. Escalados', group_codes: entity, weekday_key: day });
                                    }
                                }
                            }
                            
                            const isUpperMiddleSchool = /^[89][A-Za-z]$/.test(entity);
                            if (viewMode === 'groups' && isUpperMiddleSchool) {
                                const hasBlockAt1300 = entityEvents.some(e => e.start_time === '13:00');
                                if (!hasBlockAt1300) {
                                    entityEvents.push({ start_time: '13:00', end_time: '13:30', duration_minutes: '30', activity_type: 'community_time', display_title: 'Community Time', subject_name: 'Community Time', teacher_name: 'Prof. Escalados', group_codes: entity, weekday_key: day });
                                }
                                if (day === 'thursday') {
                                    const hasBlockAt930 = entityEvents.some(e => e.start_time === '09:30');
                                    if (!hasBlockAt930) {
                                        entityEvents.push({ start_time: '09:30', end_time: '10:30', duration_minutes: '60', activity_type: 'test', display_title: 'Cycle Test', subject_name: 'Cycle Test', teacher_name: 'Prof. Escalados', group_codes: entity, weekday_key: day });
                                    }
                                }
                            }
                            
                            return (
                              <div key={entity} className="flex-1 relative border-r border-slate-100/50 last:border-none">
                                {entityEvents.map((event, idx) => {
                                  if (!event.start_time) return null;
                                  const startMins = timeToMinutes(event.start_time);
                                  const baseMins = timeToMinutes(`${START_HOUR}:00`);
                                  const duration = parseInt(event.duration_minutes) || 60;
                                  const top = (startMins - baseMins) * PIXELS_PER_MINUTE;
                                  const height = duration * PIXELS_PER_MINUTE;
                                  const type = (event.activity_type || '').toLowerCase();
                                  const isActivity = type === 'class' || type === 'assembly';
                                  
                                  let colorClasses = getColor(viewMode === 'groups' ? event.subject_name : event.group_codes, isActivity);

                                  if (viewMode === 'teachers') {
                                    const eventGroupsArray = (event && typeof event.group_codes === 'string') ? event.group_codes.split(',').map(s => s.trim()) : [];
                                    const hasSelectedGroup = selectedGroupsForFilter.length === 0 || eventGroupsArray.some(g => selectedGroupsForFilter.includes(g));
                                    if (!hasSelectedGroup) { colorClasses += ' opacity-30 grayscale'; } 
                                  } else if (viewMode === 'groups') {
                                    const hasSelectedSubject = selectedSubjectsForFilter.length === 0 || selectedSubjectsForFilter.includes((event.subject_name || '').trim());
                                    if (!hasSelectedSubject) { colorClasses += ' opacity-30 grayscale'; } 
                                  }

                                  return (
                                    <div key={idx} className={`absolute mx-[1px] p-1 rounded border overflow-hidden flex flex-col gap-0.5 text-[9px] ${colorClasses}`} style={{ top: `${top}px`, height: `${height}px`, left: 0, right: 0 }} title={`${event.subject_name} (${event.start_time} - ${event.end_time})\nProf: ${event.teacher_name}`}>
                                      <div className="font-bold leading-tight truncate">
                                        {activeEntities.length > 1 && !isActivity ? event.activity_type : (event.display_title || event.subject_name)}
                                      </div>
                                      <div className="truncate opacity-80">{viewMode === 'groups' ? event.teacher_name : event.group_codes}</div>
                                      <div className="mt-auto opacity-70 font-mono">{event.start_time}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default NewTimetable;
